import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Waves } from "lucide-react";
import { EASE, MaskedLines, Reveal, SectionHeader } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import { IMG, STATS, PROCESS_STEPS, TESTIMONIALS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CountUp({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref}>
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden pt-[72px]">
      <div className="dot-grid pointer-events-none absolute inset-0" aria-hidden />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-[-10%] h-[560px] w-[560px] rounded-full bg-sage blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-28 lg:pt-20">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-700"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
            </span>
            NKB Traders · Global Export House
          </motion.p>

          <h1 className="mt-6 font-display text-5xl font-bold leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl">
            <MaskedLines
              delay={0.25}
              lines={[
                "From India's",
                "Heartlands,",
                <span key="w" className="italic text-forest">
                  Worldwide.
                </span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            A multi-category export house shipping agri &amp; food, textiles, handicrafts and
            industrial goods to importers in 40+ countries — lab-tested, docs-ready, on time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/products"
              data-testid="hero-catalog-cta"
              className="group flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-forest-hover hover:shadow-[0_16px_40px_-12px_rgba(6,78,59,0.55)]"
            >
              Explore the Catalog
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-quote-cta"
              className="group flex items-center gap-2 rounded-full border border-forest/25 px-7 py-3.5 text-sm font-semibold text-forest transition-all hover:bg-forest hover:text-white"
            >
              Request a Quote
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 font-mono text-[10px] font-medium uppercase tracking-[0.26em] text-slate-400"
          >
            4 Verticals · 40+ Countries · 24h Quote Response
          </motion.p>
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden rounded-t-[10rem] rounded-b-[2rem] border border-slate-200 shadow-[0_40px_80px_-40px_rgba(6,78,59,0.45)]">
              <img
                src={IMG.heroPort}
                alt="Container vessel being loaded at port"
                className="img-editorial h-[420px] w-full object-cover sm:h-[520px]"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.25, ease: EASE }}
              className="glass absolute -left-4 bottom-8 flex items-center gap-3 rounded-2xl border border-white/60 px-5 py-4 shadow-xl sm:-left-10"
              data-testid="hero-shipment-card"
            >
              <Waves className="h-8 w-8 text-forest" />
              <div>
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                  Now Sailing · Basmati Rice
                </p>
                <p className="text-sm font-semibold text-ink">Nhava Sheva → Jebel Ali</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.4, ease: EASE }}
              className="glass-dark absolute -right-3 top-8 flex items-center gap-2 rounded-full px-4 py-2.5 sm:-right-6"
              data-testid="hero-iso-badge"
            >
              <ShieldCheck className="h-4 w-4 text-gold" />
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                ISO 9001 Processes
              </span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function CategoryBento({ categories, products }) {
  const spans = [
    "lg:col-span-5 lg:row-span-2 lg:min-h-[560px]",
    "lg:col-span-7 lg:min-h-[300px]",
    "lg:col-span-4 lg:min-h-[250px]",
    "lg:col-span-3 lg:min-h-[250px]",
  ];
  return (
    <section data-testid="home-categories" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHeader
          overline="What We Ship"
          title="Four verticals. One accountable desk."
        />
        <Reveal delay={0.2}>
          <Link
            to="/products"
            data-testid="categories-view-all-link"
            className="group inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest"
          >
            Full Catalog
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-12 lg:gap-6">
        {categories.map((cat, i) => {
          const count = products.filter((p) => p.category === cat.slug).length;
          return (
            <Reveal key={cat.slug} delay={i * 0.08} className={`${spans[i % spans.length]} h-full`}>
              <Link
                to={`/products?category=${cat.slug}`}
                data-testid={`category-tile-${cat.slug}`}
                className="group relative flex h-full min-h-[300px] flex-col justify-end overflow-hidden rounded-3xl border border-slate-200"
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  loading="lazy"
                  className="img-editorial absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
                <div className="relative z-10 p-7">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">
                    0{i + 1} — {count} Product{count === 1 ? "" : "s"}
                  </p>
                  <div className="mt-2 flex items-center justify-between gap-4">
                    <h3 className="font-display text-2xl font-semibold text-white sm:text-3xl">
                      {cat.name}
                    </h3>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-all duration-300 group-hover:bg-gold group-hover:text-ink">
                      <ArrowUpRight className="h-5 w-5" />
                    </span>
                  </div>
                  <p className="mt-2 max-w-md text-sm text-white/70">{cat.tagline}</p>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function StatsBand() {
  return (
    <section data-testid="home-stats" className="relative overflow-hidden bg-ink py-20 lg:py-24">
      <div className="dot-grid-light pointer-events-none absolute inset-0" aria-hidden />
      <div className="relative mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-12 px-4 sm:px-6 lg:grid-cols-4 lg:px-8">
        {STATS.map((stat, i) => (
          <Reveal key={stat.label} delay={i * 0.1}>
            <div className="border-l border-white/15 pl-6" data-testid={`stat-${i}`}>
              <p className="font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
                <CountUp value={stat.value} suffix={stat.suffix} />
              </p>
              <p className="mt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                {stat.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Process() {
  return (
    <section data-testid="home-process" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <SectionHeader
        overline="How We Ship"
        title="From farm gate to your warehouse — five controlled steps."
        description="Every category, every consignment, follows the same disciplined pipeline. Nothing ships on hope."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5 lg:gap-5">
        {PROCESS_STEPS.map((step, i) => (
          <Reveal key={step.title} delay={i * 0.09} className="h-full">
            <div className="group relative flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-forest/40 hover:shadow-[0_24px_50px_-24px_rgba(6,78,59,0.35)]">
              <p className="font-display text-4xl font-bold text-slate-200 transition-colors group-hover:text-gold">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-5 font-display text-lg font-semibold text-ink">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{step.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section data-testid="home-testimonials" className="bg-sage/60 py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          overline="Buyer Voices"
          title="Trusted at the receiving end."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.place} delay={i * 0.1} className="h-full">
              <figure className="flex h-full flex-col rounded-3xl border border-forest/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-24px_rgba(6,78,59,0.3)]">
                <span className="font-display text-5xl leading-none text-gold">"</span>
                <blockquote className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 border-t border-slate-100 pt-4">
                  <p className="text-sm font-semibold text-ink">{t.role}</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-700">
                    {t.place}
                  </p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section data-testid="home-cta" className="relative overflow-hidden">
      <img src={IMG.crane} alt="Port cranes at dusk" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-ink/80" />
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="max-w-2xl">
          <Reveal>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">
              Ready When You Are
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl">
              Let's build your supply line.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">
              Send your requirement — grades, volumes, destination port — and get a priced,
              docs-ready offer within 24 hours.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-quote-button"
                className="rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-white"
              >
                Request a Quote
              </Link>
              <Link
                to="/products"
                data-testid="home-cta-catalog-button"
                className="rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-ink"
              >
                Browse Products
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      fetch(`${API}/categories`).then((r) => r.json()),
      fetch(`${API}/products`).then((r) => r.json()),
    ])
      .then(([cats, prods]) => {
        if (!mounted) return;
        setCategories(Array.isArray(cats) ? cats : []);
        setProducts(Array.isArray(prods) ? prods : []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 4);

  return (
    <div data-testid="page-home" className="bg-mist">
      <Hero />
      <Marquee />

      <section data-testid="home-featured" className="mx-auto max-w-7xl px-4 pt-20 sm:px-6 lg:px-8 lg:pt-28">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeader
            overline="Flagship Exports"
            title="Products buyers reorder."
          />
          <Reveal delay={0.2}>
            <Link
              to="/products"
              data-testid="featured-view-all-link"
              className="group inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest"
            >
              All 12 Lines
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-96 animate-pulse rounded-3xl bg-slate-200/70" />
              ))
            : featured.map((p, i) => (
                <ProductCard
                  key={p.slug}
                  product={p}
                  index={i}
                  onRequestQuote={() => (window.location.href = `/contact?product=${p.slug}`)}
                />
              ))}
        </div>
      </section>

      <CategoryBento categories={categories} products={products} />
      <StatsBand />
      <Process />
      <Testimonials />
      <CtaBanner />
    </div>
  );
}
